// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import { Action } from "redux"
import {
  DeclineTagSuggestionRequest,
  DeclineTagSuggestionResponse,
} from "generated/wisetime/user/timeline/v4/timeline_pb"
import * as Actions from "../actions"
import { grpc } from "app/rpc/executor"
import { timelineClient } from "app/rpc/client"
import * as TimelineSlice from "../slices/timeline"
import { patchDevelopmentUrl } from "../devUrls"
import { isGrpcError } from "../../../lib/rpcutils"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type DeclineTagSuggestionRpc = (request: DeclineTagSuggestionRequest) => Observable<DeclineTagSuggestionResponse>

export const declineTagSuggestion = (declineTagSuggestionRpc: DeclineTagSuggestionRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.declineTagSuggestion.match),
      mergeMap(action => of(action.payload)
        .pipe(
          mergeMap(declineTagSuggestionRpc),
          map(response => TimelineSlice.updateTimeRowsInCache(
            response.getUpdatedTimeRowsList()
              .map(row => patchDevelopmentUrl(row.toObject()))
          )),
          catchError(handleError),
        )
      ),
    )

const declineTagSuggestionRpc = (request: DeclineTagSuggestionRequest): Observable<DeclineTagSuggestionResponse> =>
  grpc(() => timelineClient.declineTagSuggestion(request, null))

const handleError = (e: unknown): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return of(showSnackBar({
        message: t({ id: "msg_decline_tag_suggestion_failed_not_found", message: "Failed to reject suggested tag. Could not find the target time row." }),
        messageType: "ERROR",
      }))
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({ id: "msg_decline_tag_suggestion_failed_permission_denied", message: "Failed to reject suggested tag. You do not have permission to reject a suggested tag to the time row." }),
        messageType: "ERROR",
      }))
    }
    if (code === StatusCode.FAILED_PRECONDITION) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_decline_tag_suggestion_failed_precondition", message: "Failed to decline suggested tag. Time row may be out of sync." }),
          messageType: "ERROR"
        })),
        of(Actions.onTimelineOutOfSync())
      )
    }
  }

  return of(showSnackBar({
    message: t({ id: "msg_decline_tag_suggestion_failed", message: "Failed to reject suggested tag. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

export default declineTagSuggestion(declineTagSuggestionRpc)
